// Smarter Tracking Domain Configuration
export const smarterTrackingConfig = {
  // Basic domain information
  id: 'smarter-tracking',
  name: 'Smarter Tracking',
  tagline: 'Intelligent Tracking for Smart Business',
  description: 'Transform your operations with our cutting-edge tracking solutions.',
  
  // Assets
  logo: '/assets/logos/smarter-tracking-logo.jpg',
  whiteLogo: '/assets/logos/smarter-tracking-white-logo.jpg',
  favicon: '/assets/favicons/smarter-tracking-favicon.png',
  icon: '/assets/icons/smarter-tracking',
  
  // Navigation
  pages: ['home', 'solutions', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },
                  
  // Shop link
  shopLink: 'https://shop.fleethoster.com',

  // UI Configuration
  variant: 'landing',
  showFeatures: false,
  showTestimonials: false,
  
  // Content sections
  hero: {
    title: 'Smarter Tracking for Smart Business',
    subtitle: 'Get real-time insights and optimize your business performance with our comprehensive tracking platform.',
    features: ['Real-time Tracking', 'Smart Analytics', 'Fleet Management']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'Smarter Tracking Solutions Gallery',
    subtitle: 'Explore our intelligent tracking and fleet management solutions',
    description: 'See how Smarter Tracking transforms operations with cutting-edge technology'
  },

  features: {
    title: 'Smart Tracking Features',
    subtitle: 'Intelligent Asset Management',
    description: 'Advanced tracking solutions with AI-powered insights for optimal fleet performance.',
    items: [
      {
        title: 'Real-time GPS Tracking',
        description: 'Precise real-time GPS tracking with detailed location history and route optimization.',
        icon: '📍'
      },
      {
        title: 'Predictive Analytics',
        description: 'AI-powered predictive analytics for maintenance scheduling and performance optimization.',
        icon: '📈'
      },
      {
        title: 'Fuel Management',
        description: 'Comprehensive fuel management with consumption tracking and efficiency optimization.',
        icon: '⛽'
      },
      {
        title: 'Driver Behavior Analysis',
        description: 'Advanced driver behavior analysis with safety scoring and improvement recommendations.',
        icon: '👨‍✈️'
      }
    ]
  },
  
  solutions: {
    title: 'Smarter Tracking Solutions',
    subtitle: 'Tailored solutions for your business needs',
    description: 'Discover our comprehensive range of solutions designed to optimize your operations and drive business growth.',
    sections: [
      {
        title: 'Core Solutions & Integration',
        description: 'Essential solutions that form the foundation of your business operations. Our scalable architecture ensures easy integration with your existing systems while providing 24/7 support and custom configuration options.'
      },
      {
        title: 'Advanced Features & Analytics',
        description: 'Advanced features and capabilities to enhance your business performance. Leverage AI analytics, automation tools, real-time monitoring, and predictive insights to optimize your operations and drive growth.'
      },
      {
        title: 'Enterprise Support & Custom Development',
        description: 'Comprehensive support and services for enterprise-level implementations. Our dedicated support team provides training programs, custom development services, and SLA guarantees to ensure your success.'
      }
    ]
  },
  
  blog: {
    title: 'Industry Insights',
    subtitle: 'Latest trends and insights in fleet management technology',
    description: 'Stay informed with the latest developments in fleet management, technology, and industry best practices.',
    posts: [
      {
        title: 'The Future of Fleet Management Technology',
        excerpt: 'Exploring emerging technologies that will shape the future of fleet operations.',
        author: 'Industry Expert',
        date: '2024-01-15',
        readTime: '8 min read',
        category: 'Technology',
        featured: true
      },
      {
        title: 'Best Practices for Fleet Optimization',
        excerpt: 'Proven strategies for optimizing fleet operations and reducing costs.',
        author: 'Fleet Manager',
        date: '2024-01-10',
        readTime: '6 min read',
        category: 'Best Practices',
        featured: false
      },
      {
        title: 'Data-Driven Fleet Decisions',
        excerpt: 'How to leverage data analytics for better fleet management decisions.',
        author: 'Analytics Expert',
        date: '2024-01-05',
        readTime: '7 min read',
        category: 'Analytics',
        featured: false
      }
    ]
  },
  
  testimonials: [
    {
      content: "Smarter Tracking has revolutionized how we manage our fleet. The real-time insights are invaluable for our day-to-day operations.",
      author: "John Smith",
      role: "Fleet Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      content: "The analytics capabilities have helped us reduce costs and improve efficiency significantly. One of the best investments we've made.",
      author: "Sarah Johnson",
      role: "Operations Director",
      company: "",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg"
    }
  ],

  blog: {
    title: 'Smarter Tracking Blog',
    subtitle: 'Latest News and Insights',
    description: 'Stay informed with the latest news and insights in fleet management and industry best practices.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'Smarter Tracking | Intelligent Asset & Fleet Management',
    description: 'Transform operations with cutting-edge tracking: real-time GPS, fleet management, and business intelligence for optimal performance.',
    keywords: [
      'smarter tracking', 'fleet tracking', 'GPS tracking', 'asset management', 'fleet management', 
      'tracking solutions', 'business intelligence', 'fleet optimization', 'real-time tracking',
      'fleet analytics', 'vehicle tracking', 'fleet monitoring', 'tracking technology',
      'fleet solutions', 'asset tracking', 'fleet intelligence', 'tracking platform'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'Smarter Tracking - Intelligent Asset Management Solutions',
    ogDescription: 'Advanced tracking solutions for fleet optimization and business intelligence. Real-time insights and performance analytics.',
    ogType: 'website',
    ogUrl: 'https://smartertracking.com',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'Smarter Tracking - Intelligent Asset Management Solutions',
    twitterDescription: 'Advanced tracking solutions for fleet optimization and business intelligence. Real-time insights and performance analytics.',
    
    // Robots
    robots: 'index, follow',
    author: 'Smarter Tracking',
    publisher: 'Smarter Tracking',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'Smarter Tracking | Intelligent Asset & Fleet Management',
        description: 'Transform your operations with cutting-edge tracking solutions. Real-time GPS tracking, fleet management, and business intelligence.',
        keywords: ['smarter tracking', 'fleet tracking', 'asset management', 'business intelligence', 'fleet optimization']
      },
      solutions: {
        title: 'Fleet Tracking Solutions - Smarter Tracking Services',
        description: 'Comprehensive fleet tracking solutions and asset management services. Real-time monitoring, analytics, and optimization tools.',
        keywords: ['fleet tracking solutions', 'asset management services', 'fleet monitoring', 'tracking analytics']
      },
      blog: {
        title: 'Smarter Tracking News & Insights - Fleet Management Blog',
        description: 'Latest news, insights, and best practices for fleet tracking and asset management. Industry updates and optimization tips.',
        keywords: ['smarter tracking news', 'fleet management insights', 'tracking best practices', 'fleet optimization tips']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
